import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { getAllCurrenciesApi } from "../../services/apiService";

const CurrencyContext = createContext();

const GLOBAL_CURRENCY_SYMBOLS = {
  INR: "₹",
  USD: "$",
  EUR: "€",
  GBP: "£",
  AED: "د.إ",
  SGD: "S$",
  AUD: "A$",
  CAD: "C$",
  JPY: "¥",
  CNY: "¥",
  SAR: "SAR ",
  QAR: "QAR ",
  OMR: "OMR ",
  KWD: "KWD ",
  BHD: "BHD ",
  THB: "฿",
  MYR: "RM",
  IDR: "Rp",
  PHP: "₱",
  VND: "₫",
  KRW: "₩",
  NZD: "NZ$",
  CHF: "CHF",
  SEK: "kr",
  NOK: "kr",
  DKK: "kr",
  ZAR: "R",
  RUB: "₽",
  BRL: "R$",
  TRY: "₺",
  MXN: "Mex$",
  HKD: "HK$",
  TWD: "NT$",
  PLN: "zł",
  ILS: "₪",
  EGP: "E£",
  HUF: "Ft",
  CZK: "Kč",
  RON: "lei",
  BGN: "лв",
  HRK: "kn",
  CLP: "CLP$",
  COP: "COL$",
  PEN: "S/",
  ARS: "AR$",
  PKR: "Rs",
  BDT: "৳",
  LKR: "Rs",
  NPR: "Rs",
  VUV: "VT",
  FJD: "FJ$",
  MAD: "MAD",
  DZD: "DZD",
  TND: "TND",
  NGN: "₦",
  KES: "KSh",
  GHS: "GH₵",
};

export function resolveCurrencySymbol(code, apiSymbol) {
  const upper = (code || "").toUpperCase().trim();
  
  if (apiSymbol && typeof apiSymbol === "string" && apiSymbol.trim()) {
    const s = apiSymbol.trim();
    if (upper === "INR" || s !== "₹") {
      return s;
    }
  }

  if (GLOBAL_CURRENCY_SYMBOLS[upper]) {
    return GLOBAL_CURRENCY_SYMBOLS[upper];
  }

  try {
    const parts = new Intl.NumberFormat("en-US", { style: "currency", currency: upper }).formatToParts(0);
    const symPart = parts.find((p) => p.type === "currency");
    if (symPart && symPart.value && symPart.value !== upper) {
      return symPart.value;
    }
  } catch {}

  return upper ? `${upper} ` : "$";
}

export const DEFAULT_CURRENCIES = [
  { CurrencyTypeId: 79, CurrencyCode: "INR", CurrencyName: "Indian Rupee", Symbol: "₹" },
  { CurrencyTypeId: 80, CurrencyCode: "USD", CurrencyName: "US Dollar", Symbol: "$" },
  { CurrencyTypeId: 81, CurrencyCode: "EUR", CurrencyName: "Euro", Symbol: "€" },
  { CurrencyTypeId: 82, CurrencyCode: "AED", CurrencyName: "UAE Dirham", Symbol: "د.إ" },
  { CurrencyTypeId: 83, CurrencyCode: "GBP", CurrencyName: "British Pound", Symbol: "£" },
  { CurrencyTypeId: 84, CurrencyCode: "SGD", CurrencyName: "Singapore Dollar", Symbol: "S$" },
];

export const FALLBACK_INR_RATES = {
  INR: 1,
  USD: 1 / 84,
  EUR: 1 / 91,
  GBP: 1 / 106,
  AED: 1 / 22.9,
  SGD: 1 / 62,
  AUD: 1 / 55,
  CAD: 1 / 61,
  JPY: 1.8,
  SAR: 1 / 22.4,
  QAR: 1 / 23.1,
  KWD: 1 / 273,
  BHD: 1 / 223,
  OMR: 1 / 218,
  THB: 1 / 2.3,
  MYR: 1 / 19,
};

export const convertINR = (amount, targetCurrency = "INR", roomContext = null) => {
  const num = Number(amount || 0);
  if (!num || num <= 0) return 0;
  const curr = (targetCurrency || "INR").toUpperCase();
  if (curr === "INR") return num;

  if (roomContext) {
    const p = Number(roomContext.price || roomContext.displayPrice || roomContext.roomRate || 0);
    const op = Number(roomContext.originalPrice || roomContext.baseRate || roomContext.priceInINR || 0);
    if (p > 0 && op > 0 && op > p) {
      return num * (p / op);
    }
  }

  const rate = FALLBACK_INR_RATES[curr] || 1 / 84;
  return num * rate;
};

export const CurrencyProvider = ({ children }) => {
  const [currencies, setCurrencies] = useState(DEFAULT_CURRENCIES);
  const [selectedCurrency, setSelectedCurrency] = useState(() => {
    return (typeof window !== "undefined" ? localStorage.getItem("userCurrency") : "INR") || "INR";
  });
  const [loading, setLoading] = useState(true);
  const [isCurrencyChanging, setIsCurrencyChanging] = useState(false);

  // Fetch currency master on mount
  useEffect(() => {
    let isMounted = true;
    const fetchCurrencies = async () => {
      try {
        const res = await getAllCurrenciesApi();
        console.log("[CURRENCY MASTER] 🌐 /api/GetAllCurrency RAW RESPONSE:", res);
        const list =
          res?.ResponsePacket?.CurrencyList ||
          res?.ResponsePacket?.Currencies ||
          res?.ResponsePacket ||
          res?.data ||
          [];

        if (isMounted && Array.isArray(list) && list.length > 0) {
          const normalized = list.map((c) => {
            const code = (c.CurrencyCode || c.currencyCode || c.code || "").toUpperCase();
            const rawSymbol =
              c.CurrencySymbol ||
              c.currencySymbol ||
              c.Symbol ||
              c.symbol ||
              "";
            return {
              CurrencyTypeId: c.CurrencyTypeId || c.currencyTypeId || c.id,
              CurrencyCode: code,
              CurrencyName:
                c.CurrencyName ||
                c.currencyName ||
                c.CurrencyType ||
                c.currencyType ||
                c.name ||
                code,
              Symbol: resolveCurrencySymbol(code, rawSymbol),
            };
          });
          setCurrencies(normalized);
        }
      } catch (err) {
        console.error("Failed to load currencies, using defaults:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchCurrencies();
    return () => {
      isMounted = false;
    };
  }, []);

  const changeCurrency = (code) => {
    const upper = (code || "INR").toUpperCase();
    if (upper === selectedCurrency) return;

    setIsCurrencyChanging(true);
    setSelectedCurrency(upper);
    try {
      localStorage.setItem("userCurrency", upper);
    } catch {}

    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("currency-changed", { detail: upper }));
    }

    // Keep spinner visible for smooth transition & data updates
    setTimeout(() => {
      setIsCurrencyChanging(false);
    }, 750);
  };

  const currentCurrencyObj =
    currencies.find((c) => c.CurrencyCode === selectedCurrency) ||
    DEFAULT_CURRENCIES.find((c) => c.CurrencyCode === selectedCurrency) ||
    DEFAULT_CURRENCIES[0];

  const currencySymbol = currentCurrencyObj?.Symbol || resolveCurrencySymbol(selectedCurrency);

  const formatPrice = useCallback((amount, currencyCode = selectedCurrency) => {
    const val = Number(amount || 0);
    const curr = (currencyCode || selectedCurrency).toUpperCase();
    const currObj = currencies.find((c) => c.CurrencyCode === curr) || DEFAULT_CURRENCIES.find((c) => c.CurrencyCode === curr);
    const sym = currObj?.Symbol || resolveCurrencySymbol(curr);
    const hasDec = val % 1 !== 0;

    try {
      return `${sym}${val.toLocaleString(curr === "INR" ? "en-IN" : "en-US", {
        minimumFractionDigits: hasDec ? 2 : 0,
        maximumFractionDigits: 2,
      })}`;
    } catch {
      return `${sym}${val.toFixed(hasDec ? 2 : 0)}`;
    }
  }, [currencies, selectedCurrency]);

  const convertINRAmount = (amount, roomContext = null) => {
    return convertINR(amount, selectedCurrency, roomContext);
  };

  return (
    <CurrencyContext.Provider
      value={{
        currencies,
        selectedCurrency,
        currencySymbol,
        currentCurrencyObj,
        changeCurrency,
        formatPrice,
        convertINR: convertINRAmount,
        loading,
        isCurrencyChanging,
        setIsCurrencyChanging,
      }}
    >
      {children}
      {isCurrencyChanging && typeof document !== "undefined" && (
        ReactDOM.createPortal(
          <div
            className="ls-currency-changing-overlay"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0, 0, 0, 0.72)",
              backdropFilter: "blur(5px)",
              WebkitBackdropFilter: "blur(5px)",
              zIndex: 2147483647,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "all",
            }}
            aria-live="polite"
            aria-busy="true"
          >
            <style>{`
              @keyframes ls-curr-spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
            <div
              className="ls-currency-spinner-box"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "16px",
                background: "rgba(14, 17, 24, 0.96)",
                border: "1px solid rgba(255, 255, 255, 0.22)",
                padding: "26px 36px",
                borderRadius: "18px",
                boxShadow: "0 16px 40px rgba(0, 0, 0, 0.85)",
              }}
            >
              <div
                className="ls-currency-spinner"
                style={{
                  width: "40px",
                  height: "40px",
                  border: "3.5px solid rgba(255, 255, 255, 0.25)",
                  borderTopColor: "#ffffff",
                  borderRadius: "50%",
                  animation: "ls-curr-spin 0.75s linear infinite",
                }}
              />
              <span
                className="ls-currency-spinner-text"
                style={{
                  color: "#ffffff",
                  fontSize: "14px",
                  fontWeight: 600,
                  letterSpacing: "0.4px",
                }}
              >
                Updating prices...
              </span>
            </div>
          </div>,
          document.body
        )
      )}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    return {
      currencies: DEFAULT_CURRENCIES,
      selectedCurrency: "INR",
      currencySymbol: "₹",
      currentCurrencyObj: DEFAULT_CURRENCIES[0],
      changeCurrency: () => {},
      formatPrice: (amount) => `₹${Math.round(amount || 0).toLocaleString("en-IN")}`,
      convertINR: (amount, roomContext) => convertINR(amount, "INR", roomContext),
      loading: false,
      isCurrencyChanging: false,
      setIsCurrencyChanging: () => {},
    };
  }
  return context;
};

export default CurrencyContext;
